import { useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 19 — Bulk admin actions bar. Sits above the record table when >0 rows
 * are selected. Admin-only (server-enforced via require_role("admin")).
 *
 * Props:
 *   collection: "credentials" | "licenses" | "schools" | "school_applications" | "applications" | "users"
 *   selection:  the return value from useSelection(rows)
 *   onDone:     () => void  — parent re-fetch hook after any bulk action
 *
 * For `users`, the endpoint path is `/admin/users/bulk-*`; for everything else
 * it's `/admin/records/{collection}/bulk-*`.
 */
export function BulkActionsBar({ collection, selection, onDone }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [purgeText, setPurgeText] = useState("");
  const [working, setWorking] = useState(false);

  if (selection.count === 0) return null;
  const ids = Array.from(selection.selected);

  const endpoint = (verb) =>
    collection === "users" ? `/admin/users/${verb}` : `/admin/records/${collection}/${verb}`;

  const runDelete = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(endpoint("bulk-delete"), { ids, reason });
      toast.success(`${data.deleted} record(s) deleted`);
      selection.clear();
      setDeleteOpen(false);
      setReason("");
      onDone?.();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setWorking(false); }
  };

  const runPurge = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(
        endpoint("bulk-purge"),
        { ids, reason, confirmation: purgeText },
        { headers: { "X-Confirm-Purge": "yes" } }
      );
      toast.success(`${data.purged} record(s) permanently purged`);
      selection.clear();
      setPurgeOpen(false);
      setPurgeText("");
      setReason("");
      onDone?.();
    } catch (e) { toast.error(errMessage(e)); }
    finally { setWorking(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-md" data-testid={`bulk-bar-${collection}`}>
      <span className="text-sm font-medium text-amber-900 mr-2" data-testid="bulk-selected-count">
        {selection.count} record{selection.count === 1 ? "" : "s"} selected
      </span>
      <Button variant="ghost" size="sm" onClick={selection.clear} className="text-amber-800 hover:bg-amber-100" data-testid="bulk-clear-selection">
        <X className="h-3.5 w-3.5 mr-1" /> Deselect
      </Button>
      <div className="flex-1" />
      <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="bulk-delete-btn">
        <Trash2 className="h-4 w-4 mr-1.5" /> Delete selected
      </Button>
      <Button variant="outline" size="sm" onClick={() => setPurgeOpen(true)} className="border-red-500 text-red-700 hover:bg-red-100" data-testid="bulk-purge-btn">
        <ShieldAlert className="h-4 w-4 mr-1.5" /> Purge permanently
      </Button>

      <AlertDialog open={deleteOpen} onOpenChange={(o) => !o && setDeleteOpen(false)}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selection.count} record{selection.count === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleted records are removed from all admin lists and public views. They can be restored later by a system administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-delete-reason" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={working} onClick={runDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="bulk-delete-confirm">
              Delete {selection.count} record{selection.count === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeOpen} onOpenChange={(o) => !o && setPurgeOpen(false)}>
        <AlertDialogContent data-testid="bulk-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Purge {selection.count} record{selection.count === 1 ? "" : "s"} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected record{selection.count === 1 ? "" : "s"} and cannot be undone.
              A snapshot is written to the audit log before deletion.
              Type <b>PURGE</b> in the box below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-purge-reason" />
            </div>
            <div>
              <Label className="text-xs">Type PURGE to confirm</Label>
              <Input value={purgeText} onChange={(e) => setPurgeText(e.target.value)} placeholder="PURGE" data-testid="bulk-purge-text" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={working || purgeText.trim().toUpperCase() !== "PURGE"}
              onClick={runPurge}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="bulk-purge-confirm"
            >
              Purge {selection.count} record{selection.count === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
